(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  const Carousel = global.NewHomeSource.Carousel;

  if (!Carousel ||
      !Carousel.NodeRects
  ) {
    return;
  }

  class ResizeHandler {
    /**
     * Initializes a new instance of the class.
     *
     * @constructor
     * @property {any} state - The current state of the carousel.
     * @property {any} containerSize - The size of the carousel container.
     * @property {Array} slideSizes - An array containing the sizes of each slide.
     * @property {ResizeObserver|null} resizeObserver - The observer for resize events.
     * @property {boolean} destroyed - Indicates whether the instance has been destroyed.
     */
    constructor() {
      this.state = null;
      this.containerSize = null;
      this.slideSizes = [];
      this.resizeObserver = null;
      this.destroyed = false;
      // Threshold for triggering a resize event, this value is safe when we hide the scrollbar, and a resize
      // shouldn't be triggered when the scrollbar appears or disappears.
      this.threshold = 20;
    }

    /**
     * Initializes the object with the provided state.
     *
     * @param {Object} state - The state object to initialize with.
     * @returns {this} The current instance for chaining.
     */
    initialize(state) {
      this.state = state;
      return this;
    }

    /**
     * Reads and returns the size of a node rectangle based on the current axis.
     *
     * @param {DOMRect} nodeRect - The rectangle object representing the node's dimensions.
     * @returns {number} The measured size of the node along the specified axis.
     */
    readSize(nodeRect) {
      const axis = this.state.get('axis');
      return axis.measureSize(nodeRect);
    }

    /**
     * Initializes the resize handler for the carousel component.
     * Sets up a ResizeObserver to monitor changes in size for the container and slides,
     * and updates internal size state accordingly. Observation is only started if
     * the 'watchResize' state is enabled.
     *
     * @returns {void}
     */
    create() {
      const watchResize = this.state.get('watchResize');
      const container = this.state.get('container');
      const containerRect = this.state.get('containerRect');
      const slides = this.state.get('slides');
      const slideRects = this.state.get('slideRects');
      const observeNodes = [container].concat(slides);

      if (!watchResize) {
        return;
      }

      this.containerSize = this.readSize(containerRect);
      this.slideSizes = slideRects.map((slide) => this.readSize(slide));
      this.resizeObserver = new ResizeObserver((entries) => {
        if (watchResize) {
          this.handlerResize(entries);
        }
      });
      window.requestAnimationFrame(() => {
        observeNodes.forEach((node) => this.resizeObserver.observe(node));
      });
    }

    /**
     * Handles resize events for the carousel container and its slides.
     * Compares the new size of the container and slides to their previous sizes,
     * and notifies observers to re-initialize if a significant size change is detected.
     *
     * @async
     * @param {ResizeObserverEntry[]} entries - Array of ResizeObserverEntry objects representing
     * elements that have changed size.
     * @returns {Promise<void>} Resolves when resize handling is complete.
     */
    async handlerResize(entries) {
      const container = this.state.get('container');
      const slides = this.state.get('slides');
      const newContainerRect = await ResizeHandler.getNodeRects(container);
      const newSlideRects = await Promise.all(
        slides.map((slide) => ResizeHandler.getNodeRects(slide))
      );
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (this.destroyed) {
          return;
        }

        const isContainer = entry.target === container;
        const slideIndex = slides.indexOf(entry.target);
        const lastSize = isContainer ? this.containerSize : this.slideSizes[slideIndex];
        const newSize = this.readSize(isContainer ? newContainerRect : newSlideRects[slideIndex]);
        const diffSize = Math.abs(newSize - lastSize);
        if (diffSize >= this.threshold) {
          this.state.notifyObservers('reInitialize');
          break;
        }
      }
    }

    /**
     * Cleans up resources used by the instance.
     * Sets the destroyed flag to true and disconnects the resize observer if it exists.
     */
    destroy() {
      this.destroyed = true;
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    /**
     * Returns the measured rectangle(s) of a given DOM node.
     *
     * @param {HTMLElement} node - The DOM node to measure.
     * @returns {Object} The measured rectangle(s) of the node.
     */
    static getNodeRects(node) {
      const nodeRects = new Carousel.NodeRects();
      nodeRects.setNode(node);
      return nodeRects.measure();
    }
  }

  global.NewHomeSource.Carousel.ResizeHandler = ResizeHandler;
}(window));
